import { Bar, Line } from 'react-chartjs-2';
import {
  type Scale,
  type Plugin,
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
  Legend,
  ScaleOptions,
} from 'chart.js';

const CHART_BACKGROUND = '#fafafa';

const emptyChartPlugin: Plugin<'line' | 'bar'> = {
  id: 'emptyChartPlugin',
  afterDraw(chart, _args, options) {
    if (!options.text) {
      return;
    }

    const { ctx, chartArea } = chart;
    const text = options.text;
    if (!text) return;

    ctx.save();
    ctx.font = '1rem Arial';
    ctx.fillStyle = '#888';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.fillText(text, centerX, centerY);
    ctx.restore();
  },
};

const chartAreaBackgroundPlugin: Plugin<'line' | 'bar'> = {
  id: 'chartAreaBackground',
  afterDraw: (chart) => {
    const { ctx, chartArea } = chart;

    ctx.save();
    ctx.fillStyle = CHART_BACKGROUND;
    ctx.fillRect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top,
    );
    ctx.restore();
  },
};

type AreaChartProps = {
  xAxisLabels?: string[];
  emptyChartText?: string;
  as: typeof Bar | typeof Line;
  xLabelOptions?: ScaleOptions<any>;
};

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Filler, Legend);

const defaultXaxisLabels = Array.from({ length: 30 }, (_, i) => (i + 1).toString() + '\n');

export const EmptyLineOrBarChart = ({
  as,
  xAxisLabels = defaultXaxisLabels,
  emptyChartText,
  xLabelOptions,
}: AreaChartProps) => {
  const ChartComponent = as;

  const data = {
    labels: xAxisLabels,
    datasets: [],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      x: {
        ticks: {
          rotation: 0,
          maxRotation: 0,
          callback: function (this: Scale, val: string | number) {
            if (typeof val !== 'number') return val;
            const label = this.getLabelForValue(val);
            return label.split('\n');
          },
        },
        ...xLabelOptions,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      emptyChartPlugin: {
        text: emptyChartText,
      },
    },
  };

  return (
    <ChartComponent
      data={data}
      options={options}
      plugins={[chartAreaBackgroundPlugin, emptyChartPlugin]}
    />
  );
};
